import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { financeApi as F, INVOICE_STATUS_STYLE, inr } from "@/lib/finance";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lock, Send, Printer, IndianRupee, FileMinus2, Ban } from "lucide-react";
import PaymentDialog from "@/pages/finance/PaymentDialog";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const canEdit = can("finance", "edit");
  const canApprove = can("finance", "approve");
  const canCreate = can("finance", "create");

  const load = async () => { setLoading(true); try { setInv(await F.getInvoice(id)); } catch { toast.error("Not found"); navigate("/finance"); } finally { setLoading(false); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const post = async () => { try { await F.postInvoice(id); toast.success("Invoice posted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const setStatus = async (status, note) => { try { await F.setInvoiceStatus(id, status, note); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  if (loading || !inv) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  const t = inv.totals || {};
  const isDraft = inv.status === "Draft";
  const open = ["Payment Pending", "Partially Paid"].includes(inv.status);

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      <button onClick={() => navigate("/finance")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to finance</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{inv.invoice_no}</h2>
            <Badge className={`hover:bg-inherit ${INVOICE_STATUS_STYLE[inv.status]}`} data-testid="inv-status-badge">{inv.status}</Badge>
            {inv.posted && <Badge className="bg-slate-200 text-slate-500 hover:bg-slate-200"><Lock className="mr-1 h-3 w-3" />Posted</Badge>}
          </div>
          <p className="mt-1 text-sm text-slate-500">{inv.customer?.company_name} · {inv.so_no} · Challans: {(inv.challan_nos || []).join(", ")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft && canEdit && <Button className="bg-indigo-600 text-white hover:bg-indigo-700" onClick={post} data-testid="post-invoice-btn"><Send className="mr-2 h-4 w-4" />Post Invoice</Button>}
          {inv.posted && <Button variant="outline" onClick={() => navigate(`/finance/invoices/${id}/print`)} data-testid="print-invoice-btn"><Printer className="mr-2 h-4 w-4" />Print</Button>}
          {open && canCreate && <Button variant="outline" onClick={() => setPayOpen(true)} data-testid="add-payment-btn"><IndianRupee className="mr-2 h-4 w-4" />Record Payment</Button>}
          {inv.posted && inv.status !== "Cancelled" && canCreate && <Button variant="outline" onClick={() => setNoteOpen(true)} data-testid="add-note-btn"><FileMinus2 className="mr-2 h-4 w-4" />Credit / Debit Note</Button>}
          {inv.status === "Paid" && canApprove && <Button variant="outline" onClick={() => setStatus("Closed")} data-testid="close-invoice-btn"><Lock className="mr-2 h-4 w-4" />Close</Button>}
          {["Draft", "Payment Pending"].includes(inv.status) && canApprove && <Button variant="outline" className="text-rose-600" onClick={() => setCancelOpen(true)} data-testid="cancel-invoice-btn"><Ban className="mr-2 h-4 w-4" />Cancel</Button>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading text-base font-semibold text-slate-900">Line items</h3>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Description</TableHead>
                <TableHead className="text-right text-xs uppercase text-slate-500">Qty</TableHead>
                <TableHead className="text-right text-xs uppercase text-slate-500">Rate</TableHead>
                <TableHead className="text-right text-xs uppercase text-slate-500">Taxable</TableHead>
                <TableHead className="text-right text-xs uppercase text-slate-500">GST%</TableHead>
                <TableHead className="text-right text-xs uppercase text-slate-500">Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(inv.items || []).map((it) => (
                  <TableRow key={it.id} data-testid={`inv-line-${it.id}`}>
                    <TableCell className="text-slate-800">{it.description}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-600">{it.quantity} {it.unit}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-600">{inr(it.rate)}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-600">{inr(it.taxable)}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-600">{it.tax_pct}%</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-700">{inr(it.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex justify-end">
            <dl className="w-full max-w-xs space-y-1 text-sm">
              <Line l="Taxable value" v={inr(t.taxable_value)} />
              {inv.tax_mode === "intra" ? (<><Line l="CGST" v={inr(t.cgst)} /><Line l="SGST" v={inr(t.sgst)} /></>) : <Line l="IGST" v={inr(t.igst)} />}
              {t.tcs > 0 && <Line l="TCS" v={inr(t.tcs)} />}
              {t.round_off !== 0 && <Line l="Round off" v={inr(t.round_off)} />}
              <Line l="Grand total" v={inr(t.grand_total)} bold />
              <Line l="Paid" v={inr(inv.paid_amount)} />
              {inv.credit_total > 0 && <Line l="Credit notes" v={`- ${inr(inv.credit_total)}`} />}
              {inv.debit_total > 0 && <Line l="Debit notes" v={`+ ${inr(inv.debit_total)}`} />}
              <Line l="Balance due" v={inr(inv.balance_amount)} bold />
            </dl>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Bill to</h3>
            <p className="mt-2 text-sm font-medium text-slate-800">{inv.customer?.company_name}</p>
            <p className="text-sm text-slate-500">GSTIN: {inv.customer?.gstin || "—"}</p>
            <p className="text-sm text-slate-500">{[inv.customer?.billing_address?.line1, inv.customer?.billing_address?.city, inv.customer?.billing_address?.state, inv.customer?.billing_address?.pincode].filter(Boolean).join(", ") || "—"}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div><p className="text-xs text-slate-400">Invoice date</p><p className="text-slate-700">{inv.invoice_date}</p></div>
              <div><p className="text-xs text-slate-400">Due date</p><p className="text-slate-700">{inv.due_date}</p></div>
              <div><p className="text-xs text-slate-400">Place of supply</p><p className="text-slate-700">{inv.place_of_supply || "—"}</p></div>
              <div><p className="text-xs text-slate-400">Terms</p><p className="text-slate-700">{inv.customer?.payment_terms || "—"}</p></div>
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Payments &amp; notes</h3>
            <div className="mt-3 space-y-2 text-sm">
              {(inv.payments || []).map((p) => (
                <div key={p.id} className="flex justify-between border-b border-slate-100 pb-1" data-testid={`inv-payment-${p.payment_no}`}><span className="text-slate-600">{p.payment_no} · {p.mode}</span><span className="tabular-nums text-emerald-600">{inr((p.allocations || []).filter((a) => a.invoice_id === id).reduce((s, a) => s + a.amount, 0))}</span></div>
              ))}
              {(inv.credit_debit_notes || []).map((n) => (
                <div key={n.id} className="flex justify-between border-b border-slate-100 pb-1" data-testid={`inv-note-${n.note_no}`}><span className="text-slate-600">{n.note_no} · {n.type}</span><span className={`tabular-nums ${n.type === "credit" ? "text-rose-600" : "text-amber-600"}`}>{inr(n.amount)}</span></div>
              ))}
              {(inv.payments || []).length === 0 && (inv.credit_debit_notes || []).length === 0 && <p className="text-slate-400">No payments or notes yet.</p>}
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900">History</h3>
        <div className="mt-4 space-y-3">
          {[...(inv.history || [])].reverse().map((h, i) => (
            <div key={i} className="flex gap-3 border-b border-slate-100 pb-3" data-testid="inv-history-item">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-400" />
              <div><p className="text-sm text-slate-700"><span className="font-medium">{h.status}</span>{h.note ? ` · ${h.note}` : ""}</p><p className="text-xs text-slate-400">{h.by} · {new Date(h.at).toLocaleString()}</p></div>
            </div>
          ))}
        </div>
      </Card>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} onDone={load} presetCustomerId={inv.customer_id} />
      <NoteDialog open={noteOpen} onOpenChange={setNoteOpen} invoiceId={id} onDone={load} />
      <CancelDialog open={cancelOpen} onOpenChange={setCancelOpen} onConfirm={(reason) => { setCancelOpen(false); setStatus("Cancelled", reason); }} />
    </div>
  );
}

const Line = ({ l, v, bold }) => (
  <div className={`flex justify-between ${bold ? "border-t border-slate-200 pt-1" : ""}`}><dt className="text-slate-500">{l}</dt><dd className={`tabular-nums ${bold ? "font-semibold text-slate-900" : "text-slate-700"}`}>{v}</dd></div>
);

function NoteDialog({ open, onOpenChange, invoiceId, onDone }) {
  const [type, setType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setType("credit"); setAmount(""); setReason(""); } }, [open]);
  const submit = async () => {
    if (!(Number(amount) > 0) || !reason) { toast.error("Enter amount and reason"); return; }
    setBusy(true);
    try { const r = await F.createNote({ invoice_id: invoiceId, type, amount: Number(amount), reason }); toast.success(`${r.note_no} created`); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Credit / Debit Note</DialogTitle><DialogDescription>Adjust an outstanding posted invoice. Credit reduces the receivable; debit increases it.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Type</Label>
              <Select value={type} onValueChange={setType}><SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="credit">Credit note</SelectItem><SelectItem value="debit">Debit note</SelectItem></SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="note-amount-input" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="note-reason-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-note-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ open, onOpenChange, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Cancel invoice</DialogTitle><DialogDescription>Cancellation requires authorization and a reason. An audit ledger entry is recorded.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label className="text-xs">Reason *</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" /></div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Keep</Button><Button className="bg-rose-600 text-white hover:bg-rose-700" disabled={!reason} onClick={() => onConfirm(reason)} data-testid="confirm-cancel-btn">Cancel invoice</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
